import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { AlertTypes, Button, InputRadio, Modal } from '@/components/shared';
import { useSegmentation } from '@/contexts/SegmentationContext';
import {
	getListApplicationRegistrationsGroupedQueryKey,
	getListApplicationRegistrationsWithoutFilterUnitQueryKey,
} from '@/generated/application-registrations';
import { type HttpValidationProblemDetails } from '@/generated/data.schemas';
import { getGetFilterRegistrationQueryKey, useMoveFilterRegistration } from '@/generated/filter-registration';
import { getGetRegistrationCompositeQueryKey, useMoveRegistrationComposite } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import { MoveCompositeFields } from './MoveCompositeFields';
import { MoveFilterUnitFields } from './MoveFilterUnitFields';
import styles from './MoveModal.module.scss';
import { type MoveType } from './types';

interface MoveModalProps {
	accountId: number;
	registrationCompositeId: number;
	/**
	 * Null or undefined when the composite has no filter unit, which gates the
	 * "move filter unit" option. The API sends an explicit null rather than
	 * omitting the field, so both have to be treated as absent.
	 */
	filterUnitId?: number | null;
	/**
	 * Gates the "move filter unit and location" option, which the customer asked to
	 * be hidden rather than disabled when the composite holds no location. Absent
	 * means the caller could not tell, so the option stays hidden.
	 */
	hasLocation?: boolean;
	onClose: () => void;
	/**
	 * Called after any of the moves succeeds. Only needed by callers where the moved
	 * composite is the subject of the page underneath — see the note below.
	 */
	onMoved?: () => void;
}

/**
 * Lets a seller move a filter unit to another application — on its own or together
 * with the composite's location — or a whole registration composite to another
 * account.
 *
 * Opened from the registration rows on the account overview, where the moved
 * composite is never the subject of the page underneath, so the refreshed list
 * is all that is needed and no navigation is involved.
 *
 * Also opened from the registration composite page, where the moved composite
 * *is* the page: an account move takes it off this account, and a filter unit
 * move deletes the source outright when it holds no location and no
 * application. Those callers pass `onMoved` and navigate away.
 *
 * The shared `ActionModal` only renders a headline and a message, so this
 * builds on `Modal` directly — same approach as `SwitchCustomer`.
 */
export const MoveModal: React.FunctionComponent<MoveModalProps> = ({
	accountId,
	registrationCompositeId,
	filterUnitId,
	hasLocation,
	onClose,
	onMoved,
}) => {
	const { segmentationId } = useSegmentation();
	const { data: translations } = useGetTranslations(segmentationId);
	const queryClient = useQueryClient();
	const addNotification = useNotificationStore((state) => state.addNotification);

	const [moveType, setMoveType] = useState<MoveType | null>(null);
	const [targetCompositeId, setTargetCompositeId] = useState<number | null>(null);
	const [targetAccountId, setTargetAccountId] = useState<number | null>(null);

	const moveTranslations = translations?.data.composite.moveComposite;

	// Covers both null and undefined; see the filterUnitId prop. Keep it a const
	// alias: TypeScript narrows filterUnitId through it, so the branches below can
	// pass the id on without repeating the check.
	const hasFilterUnit = filterUnitId != null;

	// Both filter unit options move the same unit to the same kind of target; they
	// differ only in whether the location travels with it.
	const isFilterUnitMove = moveType === 'filterUnit' || moveType === 'filterUnitWithLocation';
	const movesLocation = moveType === 'filterUnitWithLocation';

	const selectMoveType = (type: MoveType) => {
		setMoveType(type);
		// Drop any target picked for another option.
		setTargetCompositeId(null);
		setTargetAccountId(null);
	};

	/** Shared across every move; only the fallback message differs. */
	const handleError = (failedMessage?: string) => (axiosError: AxiosError<HttpValidationProblemDetails | string>) => {
		const error = axiosError.response?.data;

		if (!error) {
			console.error('Unknown error', axiosError);
			addNotification({ children: failedMessage, type: AlertTypes.DANGER });
		} else if (typeof error === 'string') {
			// The move endpoints answer 404/409 with a localised message.
			addNotification({ children: error, type: AlertTypes.DANGER });
		} else if (isHttpValidationProblemDetails(error)) {
			addNotification({ children: failedMessage, type: AlertTypes.DANGER });
		} else {
			addNotification({ children: translations?.data.shared.genericErrorMsg, type: AlertTypes.DANGER });
		}
	};

	const moveFilterUnitMutation = useMoveFilterRegistration({
		mutation: {
			onSuccess: async () => {
				// refresh application registration grouped list query cache
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});

				// the target now holds a filter unit, so it drops out of the candidate list
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsWithoutFilterUnitQueryKey(segmentationId, accountId),
				});

				if (targetCompositeId !== null) {
					await queryClient.invalidateQueries({
						queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, targetCompositeId),
					});
				}

				// The source keeps its composite id but no longer its filter unit, and
				// may have been deleted outright. Marking both stale is safe because
				// neither is displayed here, so nothing refetches a deleted composite —
				// it just means the detail page is fresh if visited later.
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				if (hasFilterUnit) {
					await queryClient.invalidateQueries({
						queryKey: getGetFilterRegistrationQueryKey(
							segmentationId,
							accountId,
							registrationCompositeId,
							filterUnitId,
						),
					});
				}

				addNotification({
					children: movesLocation
						? moveTranslations?.filterUnitAndLocationMoved
						: moveTranslations?.filterUnitMoved,
					type: AlertTypes.SUCCESS,
				});

				onMoved?.();
				onClose();
			},
			onError: handleError(
				movesLocation
					? moveTranslations?.filterUnitAndLocationMoveFailed
					: moveTranslations?.filterUnitMoveFailed,
			),
		},
	});

	const moveCompositeMutation = useMoveRegistrationComposite({
		mutation: {
			onSuccess: async () => {
				// The composite leaves one account's overview and joins another's.
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
				});
				await queryClient.invalidateQueries({
					queryKey: getListApplicationRegistrationsWithoutFilterUnitQueryKey(segmentationId, accountId),
				});

				if (targetAccountId !== null) {
					await queryClient.invalidateQueries({
						queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, targetAccountId),
					});
				}

				// The composite still exists, but under the target account now, so its
				// key for this account is stale rather than refetchable.
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});

				addNotification({ children: moveTranslations?.compositeMoved, type: AlertTypes.SUCCESS });

				onMoved?.();
				onClose();
			},
			onError: handleError(moveTranslations?.compositeMoveFailed),
		},
	});

	// The filter unit branches also need the filter unit to still be there: it can
	// vanish under an open modal if the list refetches, and without this Confirm
	// stays enabled while handleConfirm matches no branch. The composite arm is
	// spelled out rather than left as the else for the same reason — otherwise a
	// null moveType reports whatever targetAccountId happens to hold.
	const hasTarget = isFilterUnitMove
		? hasFilterUnit && targetCompositeId !== null
		: moveType === 'composite' && targetAccountId !== null;
	const isPending = moveFilterUnitMutation.isPending || moveCompositeMutation.isPending;

	const handleConfirm = () => {
		if (isFilterUnitMove && hasFilterUnit && targetCompositeId !== null) {
			moveFilterUnitMutation.mutate({
				segmentationId,
				accountId,
				registrationCompositeId,
				filterUnitId,
				data: { targetRegistrationCompositeId: targetCompositeId, moveLocation: movesLocation },
			});
			return;
		}

		if (moveType === 'composite' && targetAccountId !== null) {
			moveCompositeMutation.mutate({
				segmentationId,
				accountId,
				registrationCompositeId,
				data: { targetAccountId },
			});
		}
	};

	return (
		<Modal open size={'sm'} onClose={onClose}>
			<div className={styles.wrapper}>
				<p className={styles.headline}>{moveTranslations?.headline}</p>
				<p className={styles.teaser}>{moveTranslations?.teaser}</p>

				<div className={styles.options}>
					<InputRadio
						className={styles.option}
						id={'move-type-filter-unit'}
						name={'moveType'}
						label={moveTranslations?.moveFilterUnitOption}
						checked={moveType === 'filterUnit'}
						disabled={!hasFilterUnit}
						onChange={() => selectMoveType('filterUnit')}
					/>
					{/* Hidden rather than disabled with no location: the customer asked
					    for the option not to be shown at all in that case. */}
					{hasLocation && (
						<InputRadio
							className={styles.option}
							id={'move-type-filter-unit-with-location'}
							name={'moveType'}
							label={moveTranslations?.moveFilterUnitWithLocationOption}
							checked={moveType === 'filterUnitWithLocation'}
							disabled={!hasFilterUnit}
							onChange={() => selectMoveType('filterUnitWithLocation')}
						/>
					)}
					<InputRadio
						className={styles.option}
						id={'move-type-composite'}
						name={'moveType'}
						label={moveTranslations?.moveCompositeOption}
						checked={moveType === 'composite'}
						onChange={() => selectMoveType('composite')}
					/>
				</div>

				{isFilterUnitMove && hasFilterUnit && (
					<MoveFilterUnitFields
						accountId={accountId}
						withoutLocation={movesLocation}
						value={targetCompositeId}
						onChange={setTargetCompositeId}
					/>
				)}
				{moveType === 'composite' && (
					<MoveCompositeFields accountId={accountId} value={targetAccountId} onChange={setTargetAccountId} />
				)}

				<div className={styles.actionWrapper}>
					<Button variant={'ghost'} isFullWidth onClick={onClose}>
						{translations?.data.shared.cancel}
					</Button>
					<Button
						variant={'primary'}
						isFullWidth
						disabled={moveType === null || !hasTarget}
						isLoading={isPending}
						onClick={handleConfirm}
					>
						{moveTranslations?.confirmButton}
					</Button>
				</div>
			</div>
		</Modal>
	);
};
